import { clamp, hexToRgb, luminance, mixRgb, type Rgb } from '../../engine/math';
import { keyBlendOf } from '../../engine/keyedBackdrop';
import type { ChromeTone } from '../../engine/types';

// Three key lights in the wood: a fresh morning, the golden afternoon while the
// building goes on, and a warm dusk when the lights come on.
const STOPS = {
  skyTop: ['#8EC3E4', '#9AB8D8', '#2E3466'],
  skyLow: ['#E8F2E2', '#FFE2AE', '#E89A78'],
  sunGlow: ['#FFF8E0', '#FFD890', '#FF9E6A'],
  forestFar: ['#9DBFA8', '#B6B38E', '#6A5E7E'],
  forestMid: ['#6E9C74', '#87905E', '#4A4462'],
  forestNear: ['#46744E', '#5E6E3E', '#2E2E48'],
  ground: ['#6E8A46', '#8A8040', '#3A3A3E'],
  groundDark: ['#3E5634', '#4E4A2C', '#22222E'],
  path: ['#C9B48A', '#D8B47E', '#7A6A64'],
  bark: ['#6A4E3A', '#74523A', '#3E3038'],
  barkLit: ['#9C7A5A', '#B88A5C', '#6A5050'],
  barkShade: ['#3E2C22', '#44301E', '#221A22'],
  leafDark: ['#2C5A36', '#3E5A2A', '#1E2A2E'],
  leaf: ['#4A8A48', '#6A8E3A', '#2E3E3A'],
  leafLit: ['#8CC468', '#C8C460', '#5E5E4A'],
  wood: ['#B8875A', '#C99260', '#7E5A48'],
  woodLit: ['#DDB283', '#EDC08A', '#A07A62'],
  woodShade: ['#7A5434', '#86572F', '#4A3430'],
  roof: ['#7E4A3A', '#8E4E36', '#4E3036'],
  roofLit: ['#A86A50', '#BC7048', '#6A4446'],
  rope: ['#D8C49A', '#E6C890', '#8A7A6E'],
  haze: ['#E4F0E4', '#FFE6B8', '#9E7A8E'],
} as const;

type ColorName = keyof typeof STOPS;
export type TreehouseColors = Record<ColorName, Rgb>;

export const TREEHOUSE_KEYS = 3;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = {} as Record<ColorName, Rgb[]>;
NAMES.forEach((name) => {
  PARSED[name] = STOPS[name].map(hexToRgb);
});

export const treehouseKeyColors = (index: number): TreehouseColors => {
  const key = Math.min(TREEHOUSE_KEYS - 1, Math.max(0, Math.round(index) || 0));
  const colors = {} as TreehouseColors;
  NAMES.forEach((name) => {
    colors[name] = PARSED[name][key];
  });
  return colors;
};

/** Morning into golden afternoon as the building goes up; dusk falls when it is finished. */
export const treehouseLight = (progress: number, finale: number) =>
  clamp(clamp(progress) * 0.55 + clamp(finale) * 0.45);

export const treehouseColors = (light: number): TreehouseColors => {
  const { index, amount } = keyBlendOf(light, TREEHOUSE_KEYS);
  const colors = {} as TreehouseColors;
  NAMES.forEach((name) => {
    colors[name] = mixRgb(PARSED[name][index], PARSED[name][index + 1], amount);
  });
  return colors;
};

/** The header sits under the dark canopy of the great tree, so its text stays white. */
export const treehouseChromeTone = (progress: number): ChromeTone =>
  luminance(treehouseColors(treehouseLight(progress, progress >= 0.995 ? 1 : 0)).leafDark) > 0.3 ? 'dark' : 'light';
